import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useAddNotification} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {useFormContext} from '@yandex-delivery/form'
import type {OrderFormProps, OrderFormState} from '@yandex-delivery/form/ui/OrderForm/types'
import {debounce, getErrorMessage} from '@yandex-delivery/utils'
import {useCallback, useMemo, useState} from 'react'

import {fetchPollSharing, fetchRemoveLastOrderId, fetchStoreLastOrderId} from 'src/shared/api'
import {appConfig, experiments} from 'src/shared/config'
import {OrderEventGoal, useFormUserEventHandler} from 'src/shared/hooks'

import {useAcceptOffer} from '../api'
import type {AcceptOfferResponse} from '../api/model/types'
import {store} from '../store'
import {mergeStates} from '../utils/mergeStates'
import {toFormState} from '../utils/toFormState'
import {useDefaultValuesAndDraftSync} from './useDefaultValuesAndDraftSync'
import {useGetDeliveryFormData} from './useGetDeliveryFormData'

/** Количество перезапросов в шарилку при ошибках нетворка/500 */
const SHARING_REQUEST_RETRIES = 3

/**
 * Создаёт коллбэк, вызываемый после успешного создания заказа.
 */
const getAcceptOfferSuccessHandler =
  ({
    clearDraftValues,
    onLoadingStatusChange,
    onReachGoal,
    showError,
  }: {
    /** Метод для удаления данных драфтового заказа из persistent storage */
    clearDraftValues: VoidFunction
    /** Коллбэк, вызываемый при изменении признака, что происходит выполнение запроса */
    onLoadingStatusChange: (isLoading: boolean) => void
    /** Метод для отправки данных в Метрику */
    onReachGoal: (goal: OrderEventGoal) => void
    /** Метод для показа ошибки */
    showError: (error?: Error) => void
  }) =>
  (data: AcceptOfferResponse) => {
    const deliveryId = data.delivery_ids?.[0]

    if (!deliveryId) {
      showError()
      return
    }

    onLoadingStatusChange(true)

    return fetchStoreLastOrderId(deliveryId)
      .then(() =>
        fetchPollSharing(
          {
            order_id: deliveryId,
            order_provider_id: 'cargo-claims',
          },
          SHARING_REQUEST_RETRIES,
        ),
      )
      .then(sharing => fetchRemoveLastOrderId().then(() => sharing.sharing_url))
      .then(url => {
        if (!url) {
          return
        }

        onReachGoal(OrderEventGoal.SuccessOrderCreate)
        clearDraftValues()
        window.location.href = url
      })
      .catch(showError)
      .finally(() => {
        onLoadingStatusChange(false)
      })
  }

export const useOrderForm = (): OrderFormProps => {
  const [isSharingLinkLoading, setIsSharingLinkLoading] = useState<boolean>(false)
  const vertical = useFormContext().watch('offerFlow.vertical.name')
  const settings =
    store.state.settings && Object.keys(store.state.settings).includes(vertical)
      ? store.state.settings[vertical]
      : undefined

  const addNotification = useAddNotification()

  const handleReachGoal = useFormUserEventHandler()

  const i18n = useI18n()

  const getDeliveryFormData = useGetDeliveryFormData()

  const queryClient = useQueryClient()

  const {
    data: formWidget,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['orderForm'],
    queryFn: getDeliveryFormData,
  })

  const updateFormState = useMemo(
    () =>
      debounce(async (formState: OrderFormState) => {
        store.setState(prev => ({
          ...prev,
          formRequest: {
            ...prev.formRequest,
            mode: settings?.mode,
            state: mergeStates(prev.formRequest.state, formState, settings?.defaultValues),
          },
        }))

        await queryClient.invalidateQueries({queryKey: ['orderForm']})

        setChanging(false)
      }, experiments.bduiDebounceDuration ?? 0),
    [queryClient, settings?.defaultValues, settings?.mode],
  )

  const {defaultValues, clearDraftValues, updateDraftValues} = useDefaultValuesAndDraftSync(updateFormState)

  const showError = useCallback(
    (error?: Error) => {
      addNotification(error ? getErrorMessage(error) : i18n('request.mutation.error', 'Ошибка запроса'), {
        type: 'error',
      })
    },
    [addNotification, i18n],
  )

  const handleAcceptOfferSuccess = useMemo(
    () =>
      getAcceptOfferSuccessHandler({
        clearDraftValues,
        onLoadingStatusChange: setIsSharingLinkLoading,
        onReachGoal: handleReachGoal,
        showError,
      }),
    [clearDraftValues, handleReachGoal, showError],
  )

  const {isPending: isAcceptOfferPending, mutate: mutateAcceptOffer} = useAcceptOffer({
    onError: showError,
    onSuccess: handleAcceptOfferSuccess,
  })

  const [changing, setChanging] = useState(false)

  const handleChange = useCallback(
    (formState: OrderFormState) => {
      setChanging(false)

      updateDraftValues(formState)

      return updateFormState(formState)
    },
    [updateFormState, updateDraftValues],
  )

  const handleSubmit = useCallback(
    (data: OrderFormState) => {
      if (!store.state.offerId) {
        showError()
        return
      }

      const state = toFormState({...store.state.formRequest.state, ...data})

      mutateAcceptOffer({
        mode: settings?.mode || '',
        state,
        offers: [{id: store.state.offerId}],
      })
    },
    [mutateAcceptOffer, settings?.mode, showError],
  )

  const isOverlayVisible = isAcceptOfferPending || isSharingLinkLoading
  const config = Object.assign(appConfig, {offerID: store.state.offerId})

  return {
    defaultValues,
    disableSubmit: changing,
    isFetching,
    layout: formWidget,
    isLoading,
    isOverlayVisible,
    onChange: handleChange,
    config,
    onSubmit: handleSubmit,
    outerDefaults: settings?.defaultValues,
  }
}
